import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import AdminDownbar from '../../components/ui/AdminDownbar';
import Icon from '../../components/AppIcon';
import { Pie, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
} from 'chart.js';
import useDatabase from '../../hooks/useDatabase';
import { useBibles } from '../../hooks/useBibles';
import { useThings } from '../../hooks/useThings';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement);

const AdminReports = () => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { books, loading: booksLoading, fetchBooks } = useDatabase();
  const { bibles, loading: biblesLoading, fetchBibles } = useBibles();
  const { things, loading: thingsLoading, fetchThings } = useThings();

  const loading = booksLoading || biblesLoading || thingsLoading;

  useEffect(() => {
    fetchBooks();
    fetchBibles();
    fetchThings();
  }, []);

  // Debug específico para accesorios con favoritos
  const thingsWithFavorites = things.filter(t => t.favorite_count_product > 0);
  console.log('🔧 Things with favorites:', thingsWithFavorites.length, thingsWithFavorites);

  // Combinar todos los productos con un formato unificado
  const allProducts = [
    // Libros
    ...books.map(book => ({
      id: book.id_product,
      title: book.title_product,
      favorite_count: book.favorite_count_product || 0,
      type: 'Libro',
      typeColor: '#c37546'
    })),
    // Biblias
    ...bibles.map(bible => ({
      id: bible.bible_id,
      title: bible.title_bible,
      favorite_count: bible.favorite_count_product || 0,
      type: 'Biblia',
      typeColor: '#125cbf'
    })),
    // Accesorios
    ...things.map(thing => ({
      id: thing.thing_id,
      title: thing.title_thing,
      favorite_count: thing.favorite_count_product || 0,
      type: 'Accesorio',
      typeColor: '#6aafd2'
    }))
  ];

  // Filtrar productos que tienen favoritos y ordenar
  const productsWithFavorites = allProducts.filter(p => p.favorite_count > 0);
  
  const sortedProducts = productsWithFavorites.sort((a, b) => b.favorite_count - a.favorite_count);
  const allProductsForPie = sortedProducts; // Mostrar todos los productos con favoritos

  // Paleta de colores más amplia y vibrante
  const colorPalette = [
    '#FF6B6B', // Coral rojo
    '#4ECDC4', // Turquesa
    '#45B7D1', // Azul cielo
    '#96CEB4', // Verde menta
    '#FFEAA7', // Amarillo suave
    '#DDA0DD', // Lavanda
    '#98D8C8', // Verde agua
    '#F7DC6F', // Amarillo dorado
    '#BB8FCE', // Púrpura suave
    '#85C1E9', // Azul claro
    '#F8C471', // Naranja suave
    '#82E0AA'  // Verde claro
  ];

  const pieData = {
    labels: allProductsForPie.map(p => `${p.title} (${p.type})`),
    datasets: [
      {
        data: allProductsForPie.map(p => p.favorite_count),
        backgroundColor: allProductsForPie.map((p, index) => colorPalette[index % colorPalette.length]),
        borderColor: '#ffffff',
        borderWidth: 3,
        hoverBorderWidth: 4,
        hoverBorderColor: '#333333',
      },
    ],
  };

  const lineData = {
    labels: sortedProducts.map(p => `${p.title} (${p.type})`),
    datasets: [
      {
        label: 'Favoritos por producto',
        data: sortedProducts.map(p => p.favorite_count),
        borderColor: '#FF6B6B',
        backgroundColor: 'rgba(255, 107, 107, 0.2)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: sortedProducts.map((p, index) => colorPalette[index % colorPalette.length]),
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointHoverBackgroundColor: sortedProducts.map((p, index) => colorPalette[index % colorPalette.length]),
        pointHoverBorderColor: '#333333',
        pointHoverBorderWidth: 3,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#324561',
          font: { family: 'Poppins', size: 13 },
        },
      },
      tooltip: {
        backgroundColor: '#0c3454',
        titleColor: '#ffffff',
        bodyColor: '#e8f4f8',
        cornerRadius: 4,
        titleFont: { family: 'Poppins', size: 14 },
        bodyFont: { family: 'Poppins', size: 13 },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { color: '#5e697e' },
        grid: { color: 'rgba(0,0,0,0.05)' },
      },
      x: {
        ticks: { color: '#5e697e' },
        grid: { display: false },
      },
    },
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-[150px] lg:pb-0">
      {isDesktop ? (
        <AdminSidebar
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          userName="Administrador"
          />
          ) : (
        <AdminDownbar />
      )}
      <div className={`${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'} transition-all duration-300`}>
        <header
          className="sticky top-0 z-20 p-6 border-b border-border backdrop-blur-sm"
          style={{ backgroundColor: '#fbfbfb' }}
        >
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4">
              <div className="p-2 rounded-md bg-[#c375461a]">
                <Icon name="BarChart3" size={24} className="text-[#c37546]" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-[#0c3454]">
                  Reportes y Estadísticas
                </h1>
                <p className="text-sm italic text-muted-foreground mt-1">
                  “Escribe la visión, y declárala en tablas...” — Habacuc 2:2
                </p>
              </div>
            </div>
          </div>
        </header>
        <main className="p-6">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="flex items-center gap-3 bg-white px-6 py-4 rounded shadow-sm">
                <Icon name="Loader2" size={24} className="animate-spin text-[#c37546]" />
                <span className="text-[#5e697e] font-medium">Cargando estadísticas...</span>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-7xl mx-auto">
              {/* Pie Chart */}
              <div className="bg-white border border-gray-200 rounded-md shadow-sm hover:shadow-lg transition hover:-translate-y-1">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <Icon name="PieChart" size={20} className="text-[#c37546]" />
                    <h2 className="text-lg font-semibold text-[#0c3454]">Productos Favoritos</h2>
                  </div>
                </div>
                <div className="h-[380px] px-5 py-4">
                  <Pie data={pieData} options={chartOptions} />
                </div>
              </div>

              {/* Line Chart */}
              <div className="bg-white border border-gray-200 rounded-md shadow-sm hover:shadow-lg transition hover:-translate-y-1">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <Icon name="LineChart" size={20} className="text-[#125cbf]" />
                    <h2 className="text-lg font-semibold text-[#0c3454]">Tendencia de Favoritos por Producto</h2>
                  </div>
                </div>
                <div className="h-[380px] px-5 py-4">
                  <Line data={lineData} options={chartOptions} />
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminReports;
